import { useState } from "react";
import { v2TradeExecute } from "../services/tradingAgentsV2";

interface DecisionPanelProps {
  decision?: {
    action?: string;
    confidence?: number;
    rationale?: string;
  } | null;
  runId?: string | null;
}

export function DecisionPanel({ decision, runId }: DecisionPanelProps) {
  const [tradeResult, setTradeResult] = useState<Record<string, unknown> | null>(null);
  const [trading, setTrading] = useState(false);

  if (!decision || Object.keys(decision).length === 0) {
    return (
      <p className="text-sm text-gray-500">No decision payload for this run.</p>
    );
  }

  const canTrade =
    runId &&
    decision.action &&
    decision.action.toLowerCase() !== "hold";

  const handleTrade = async () => {
    if (!runId) return;
    setTrading(true);
    try {
      const result = await v2TradeExecute({ run_id: runId });
      setTradeResult(result);
    } catch (e) {
      setTradeResult({ error: (e as Error).message });
    } finally {
      setTrading(false);
    }
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-primary-50 to-white p-4 shadow-sm">
      <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-gray-500">Action</dt>
          <dd
            className={`text-lg font-bold ${
              decision.action?.toLowerCase() === "buy"
                ? "text-green-600"
                : decision.action?.toLowerCase() === "sell"
                  ? "text-red-600"
                  : "text-gray-900"
            }`}
          >
            {(decision.action ?? "—").toUpperCase()}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Confidence</dt>
          <dd className="text-lg font-bold text-gray-900">
            {decision.confidence != null
              ? `${Math.round(Number(decision.confidence) * 100)}%`
              : "—"}
          </dd>
        </div>
        <div className="flex items-end">
          {canTrade && (
            <button
              type="button"
              onClick={handleTrade}
              disabled={trading}
              className={`rounded-lg px-4 py-2 text-sm font-medium text-white transition-colors ${
                decision.action?.toLowerCase() === "buy"
                  ? "bg-green-600 hover:bg-green-700"
                  : "bg-red-600 hover:bg-red-700"
              } disabled:opacity-50`}
            >
              {trading ? "Executing..." : `Execute ${(decision.action || "").toUpperCase()}`}
            </button>
          )}
        </div>
      </dl>
      {decision.rationale ? (
        <p className="mt-3 text-sm text-gray-700">{decision.rationale}</p>
      ) : null}
      {tradeResult && (
        <div className="mt-3 rounded-lg border border-gray-200 bg-white p-3 text-sm">
          <p className="font-medium text-gray-700">Trade Result:</p>
          <pre className="mt-1 overflow-x-auto text-xs text-gray-600">
            {JSON.stringify(tradeResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
